/**
 * The ONE reader for AppSheet yes/no values in the client.
 *
 * AppSheet's Yes/No columns reach the mirror as the strings "Y" / "N" (measured 2026-10-08 across the eight mirror
 * collections: every yes/no column held only "Y" and "N", plus one row holding "False"). AppSheet's own screens show
 * the same columns as "Yes" / "No", and an import, a bot or a platform change can store the full word or a real
 * boolean. A check written by hand for one spelling (`=== 'Y'`, `=== true`) reads every other spelling as "no" with no
 * error: `raw['Archive'] === true` showed archived tickets as open, because the column holds "Y".
 *
 * So every read of a yes/no value goes through here, and `node scripts/guards/check-appsheet-yesno.mjs` (CI job
 * `guardrails`) fails the build when a new hand-rolled comparison is added.
 */

const YES = new Set(['y', 'yes', 'true']);
const NO = new Set(['n', 'no', 'false']);

/**
 * Three answers, never two: `true` (yes), `false` (no), `null` (blank or not a yes/no value).
 * Use this where "not answered" must stay distinct from "no" (a display cell, an optional field).
 */
export function parseAppSheetYesNo(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return null;
  const token = value.trim().toLowerCase();
  if (YES.has(token)) return true;
  if (NO.has(token)) return false;
  return null;
}

/** `true` only for a yes (Y / Yes / true in any case, or boolean true). Blank and unrecognised values are `false`. */
export function parseAppSheetBoolean(value: unknown): boolean {
  return parseAppSheetYesNo(value) === true;
}
